// src/data/greenCardData.js
const greenCardData = {
  // ==================== الصفحة الرئيسية (home) ====================
  home: {
    hero: {
      badge: "Green Card Services",
      title: "U.S. Green Card Assistance",
      subtitle: "Professional help with applying, renewing, replacing, or removing conditions on your Permanent Resident Card."
    },
    content: `
      <section class="py-16 lg:py-24 bg-gradient-to-b from-blue-50 to-white">
        <div class="max-w-7xl mx-auto px-6 text-center">
          <h2 class="text-4xl md:text-5xl font-black text-gray-900 mb-8">Your Complete Green Card Solution</h2>
          <p class="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Whether you're applying for the first time, renewing an expiring card, replacing a lost one, or removing conditions — 
            we guide you step-by-step with attorney-designed packages that guarantee USCIS approval.
          </p>
          <div class="grid md:grid-cols-3 gap-8 mt-16">
            <div class="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div class="text-6xl mb-4">Apply</div>
              <h3 class="text-2xl font-bold text-gray-900 mb-3">New Green Card</h3>
              <p class="text-gray-600">Family-based, marriage, employment & more</p>
            </div>
            <div class="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div class="text-6xl mb-4">Renew</div>
              <h3 class="text-2xl font-bold text-gray-900 mb-3">10-Year Renewal</h3>
              <p class="text-gray-600">Form I-90 made simple & fast</p>
            </div>
            <div class="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div class="text-6xl mb-4">Remove Conditions</div>
              <h3 class="text-2xl font-bold text-gray-900 mb-3">From 2-Year to 10-Year</h3>
              <p class="text-gray-600">Marriage-based Form I-751</p>
            </div>
          </div>
        </div>
      </section>
    `
  },

  // ==================== Apply for a Green Card ====================
  "apply-green-card": {
    hero: {
      badge: "Green Card Application",
      title: "Apply for a Green Card",
      subtitle: "Finding Your Family-Based Path to Permanent Residency in the United States"
    },
    content: `
      <section class="py-16 lg:py-24 bg-gray-50">
        <div class="max-w-[80rem] mx-auto px-6">
          <div class="text-center mb-16">
            <h2 class="text-2xl md:text-5xl font-black text-gray-900 mb-6">Which Path Applies to You?</h2>
            <p class="text-xl text-gray-600 max-w-4xl mx-auto">The process depends on your current location</p>
          </div>

          <div class="grid lg:grid-cols-2 gap-12 mb-20">
            <!-- Inside the U.S. -->
            <div class="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden hover:shadow-2xl transition-all">
              <div class="bg-gradient-to-br from-blue-600 to-blue-700 p-10 text-white text-center">
                <h3 class="text-3xl font-bold">Adjustment of Status In U.S.</h3>
              </div>
              
              <div class="p-10">
              <p class="text-lg text-gray-700 mb-8">You are lawfully present in the U.S. and can apply without leaving</p>
                <ul class="space-y-4 text-left mb-10">
                  <li class="flex items-center gap-3 text-gray-700"><span class="text-2xl text-blue-600">✓</span> Adjustment of Status Guide</li>
                  <li class="flex items-center gap-3 text-gray-700"><span class="text-2xl text-blue-600">✓</span> Adjustment of Status Costs</li>
                  <li class="flex items-center gap-3 text-gray-700"><span class="text-2xl text-blue-600">✓</span> EXPLORE FORM I-485</li>
                </ul>
                <a href="/adjustment-of-status/i-485" class="block text-center bg-blue-600 text-white font-bold text-xl py-5 rounded-xl hover:bg-blue-700 transition">
                  Get Started → Adjustment of Status Package
                </a>
              </div>
            </div>

            <!-- Outside the U.S. -->
            <div class="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden hover:shadow-2xl transition-all">
              <div class="bg-gradient-to-br from-blue-600 to-indigo-700 p-10 text-white text-center">
                <h3 class="text-3xl font-bold">Consular Processing Outside U.S.</h3>
              </div>
              <div class="p-10">
                <p class="text-lg text-gray-700 mb-8">Your petitioner starts the process from the U.S.</p>
                <ul class="space-y-4 text-left mb-10">
                  <li class="flex items-center gap-3 text-gray-700"><span class="text-2xl text-blue-600">✓</span> Consular Processing Guide</li>
                  <li class="flex items-center gap-3 text-gray-700"><span class="text-2xl text-blue-600">✓</span> Consular Processing Costs</li>
                  <li class="flex items-center gap-3 text-gray-700"><span class="text-2xl text-blue-600">✓</span> EXPLORE FORM I-130</li>
                </ul>
                <a href="/services/i-130" class="block text-center bg-blue-600 text-white font-bold text-xl py-5 rounded-xl hover:bg-blue-700 transition">
                  Get Started → Immigrant Visa Petition Package
                </a>
              </div>
            </div>
          </div>

          <div class="bg-blue-900 rounded-3xl p-12 text-white text-center">
            <h3 class="text-3xl font-bold mb-6">Explain My Options for Family-Based Immigration</h3>
            <p class="text-xl max-w-5xl mx-auto mb-8 leading-relaxed">
              Adjustment of status and consular processing are the two basic ways to apply for a green card. 
              Most people must take consular processing. Only some can adjust status while inside the U.S.
            </p>
            <a href="/apply-green-card" class="inline-block bg-white text-blue-900 font-bold px-10 py-4 rounded-xl hover:bg-gray-100 transition text-xl">
              Learn About Family-Based Immigration →
            </a>
          </div>
        </div>
      </section>
    `
  },

  // ==================== Green Card Renewal ====================
  "green-card-renewal": {
    hero: {
      badge: "Renewal",
      title: "Green Card Renewal",
      subtitle: "Don't Let Your Card Expire — Renew Your 10-Year Permanent Resident Card"
    },
    content: `
      <section class="py-16 lg:py-24 bg-gray-50">
        <div class="max-w-7xl mx-auto px-6">
          <div class="text-center mb-16">
            <h2 class="text-4xl md:text-5xl font-black text-gray-900 mb-6">Renew Your Expiring Green Card Before It's Too Late</h2>
            <p class="text-xl text-gray-600 max-w-4xl mx-auto">An expired card can block employment, travel, and even driver's license renewal</p>
          </div>

          <div class="grid md:grid-cols-3 gap-8 mb-16">
            <div class="text-center bg-white rounded-2xl p-8 shadow-lg">
              <div class="text-5xl font-black text-red-600">763,768</div>
              <p class="text-gray-600 mt-2">I-90 applications filed yearly</p>
            </div>
            <div class="text-center bg-white rounded-2xl p-8 shadow-lg">
              <div class="text-5xl font-black text-orange-600">84,014</div>
              <p class="text-gray-600 mt-2">Rejected annually</p>
            </div>
            <div class="text-center bg-white rounded-2xl p-8 shadow-lg">
              <div class="text-5xl font-black text-red-700">29,649</div>
              <p class="text-gray-600 mt-2">Denied annually</p>
            </div>
          </div>

          <div class="bg-red-50 border-2 border-red-300 rounded-3xl p-10 text-center mb-16">
            <p class="text-2xl font-bold text-red-800">You MUST renew if your card expires in the next 6 months or is already expired</p>
          </div>

          <div class="bg-white rounded-3xl shadow-xl p-12 mb-16">
            <h3 class="text-3xl font-bold text-gray-900 mb-8 text-center">Step-by-Step Renewal Process</h3>
            <ol class="grid md:grid-cols-5 gap-6 text-center">
              <li class="flex flex-col items-center"><span class="text-6xl mb-3">1</span><p class="font-semibold">Gather Documents</p></li>
              <li class="flex flex-col items-center"><span class="text-6xl mb-3">2</span><p class="font-semibold">File Form I-90</p></li>
              <li class="flex flex-col items-center"><span class="text-6xl mb-3">3</span><p class="font-semibold">Pay Fees</p></li>
              <li class="flex flex-col items-center"><span class="text-6xl mb-3">4</span><p class="font-semibold">Biometrics</p></li>
              <li class="flex flex-col items-center"><span class="text-6xl mb-3">5</span><p class="font-semibold">Receive New Card</p></li>
            </ol>
          </div>

          <div class="bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-3xl p-16 text-center">
            <h3 class="text-4xl font-bold mb-6">Get Your Renewal Done in 15 Minutes</h3>
            <p class="text-2xl mb-10">Attorney-designed • 100% Approval Guarantee</p>
            <a href="/green-card/i-90" class="inline-block bg-white text-blue-600 font-bold text-2xl px-12 py-6 rounded-xl hover:bg-gray-100 transition">
              Start My I-90 Package Now →
            </a>
          </div>

          <div class="mt-20 grid md:grid-cols-2 gap-12">
            <div class="bg-amber-50 rounded-2xl p-10 border-2 border-amber-300">
              <h4 class="text-2xl font-bold text-amber-900 mb-4">Temporary Extension (36 Months)</h4>
              <p class="text-gray-700">After filing I-90, USCIS sends I-797C receipt that extends your status 36 months with expired card</p>
            </div>
            <div class="bg-emerald-50 rounded-2xl p-10 border-2 border-emerald-300">
              <h4 class="text-2xl font-bold text-emerald-900 mb-4">ADIT Stamp for Urgent Travel</h4>
              <p class="text-gray-700">Get official stamp in passport at USCIS office — valid like a green card</p>
            </div>
          </div>
        </div>
      </section>
    `
  },

  // ==================== Green Card Replacement ====================
  "green-card-replacement": {
    hero: {
      badge: "Replacement",
      title: "Green Card Replacement",
      subtitle: "Lost, Stolen, Damaged, or Incorrect Data? Get a New Card Fast"
    },
    content: `
      <section class="py-16 lg:py-24 bg-gray-50">
        <div class="max-w-7xl mx-auto px-6 text-center">
          <h2 class="text-4xl md:text-5xl font-black text-gray-900 mb-8">Replace Your Lost or Damaged Green Card Immediately</h2>
          <div class="bg-red-100 border-2 border-red-400 rounded-3xl p-10 max-w-4xl mx-auto mb-16">
            <p class="text-2xl font-bold text-red-800">U.S. law requires you to carry valid proof of status at all times (age 18+)</p>
          </div>

          <div class="grid md:grid-cols-3 gap-8 mb-16">
            <div class="bg-white rounded-2xl p-10 shadow-lg border border-gray-200">
              <div class="text-6xl mb-4">Lost/Stolen</div>
              <p class="text-gray-700">File I-90 immediately</p>
            </div>
            <div class="bg-white rounded-2xl p-10 shadow-lg border border-gray-200">
              <div class="text-6xl mb-4">Damaged</div>
              <p class="text-gray-700">Replace mutilated card</p>
            </div>
            <div class="bg-white rounded-2xl p-10 shadow-lg border border-gray-200">
              <div class="text-6xl mb-4">USCIS Error</div>
              <p class="text-gray-700">Free replacement if their mistake</p>
            </div>
          </div>

          <div class="bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-3xl p-16">
            <h3 class="text-4xl font-bold mb-6">Same Form I-90 — Same Easy Process</h3>
            <a href="/green-card/i-90" class="inline-block bg-white text-blue-600 font-bold text-2xl px-12 py-6 rounded-xl hover:bg-gray-100 transition">
              Start Replacement Package →
            </a>
          </div>

          <div class="mt-20 grid md:grid-cols-2 gap-12">
            <div class="bg-white rounded-2xl p-10 shadow-lg">
              <h4 class="text-2xl font-bold text-gray-900 mb-4">Name Change?</h4>
              <p class="text-gray-700">Update your name (marriage, divorce, court order) during replacement — just attach proof</p>
            </div>
            <div class="bg-white rounded-2xl p-10 shadow-lg">
              <h4 class="text-2xl font-bold text-gray-900 mb-4">Criminal Record?</h4>
              <p class="text-gray-700">Even arrests without conviction can trigger denial — get evaluated first</p>
            </div>
          </div>
        </div>
      </section>
    `
  },

  // ==================== Form I-90 Package ====================
  "i-90": {
    hero: {
      badge: "Form I-90 Package",
      title: "Green Card Renewal & Replacement",
      subtitle: "Attorney-Designed • Approval Guaranteed • Only $99"
    },
    content: `
      <section class="py-16 lg:py-32 bg-gradient-to-b from-blue-50 to-white">
        <div class="max-w-5xl mx-auto px-6 text-center">
          <div class="bg-white rounded-3xl shadow-2xl p-16 border border-blue-200">
            <p class="text-sm font-semibold text-blue-600 mb-4">Our Price vs Attorney</p>
            <div class="text-8xl font-black text-gray-900 mb-4">$99 <span class="text-3xl text-gray-500">+ USCIS fees</span></div>
            <p class="text-3xl font-bold text-green-600 mb-10">Save $850+ compared to average attorney</p>

            <div class="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-3xl p-12 mb-12">
              <h3 class="text-4xl font-bold mb-10">Your Package Includes Everything:</h3>
              <ul class="grid md:grid-cols-2 gap-6 text-left text-xl">
                <li class="flex items-center gap-4"><span class="text-3xl">✓</span> Form I-90 (Renewal/Replacement)</li>
                <li class="flex items-center gap-4"><span class="text-3xl">✓</span> Form G-1145 (e-Notification)</li>
                <li class="flex items-center gap-4"><span class="text-3xl">✓</span> Personalized Filing Instructions</li>
                <li class="flex items-center gap-4"><span class="text-3xl">✓</span> Document Checklist</li>
                <li class="flex items-center gap-4"><span class="text-3xl">✓</span> Live Expert Support</li>
                <li class="flex items-center gap-4"><span class="text-3xl">✓</span> USCIS Approval Guaranteed or Money Back</li>
              </ul>
            </div>

            <a href="#" class="inline-block bg-blue-600 text-white font-bold text-2xl px-16 py-6 rounded-xl hover:bg-blue-700 transition shadow-lg">
              Start My I-90 Package Now →
            </a>
          </div>
        </div>
      </section>
    `
  },

  // ==================== Form I-751 Package ====================
  "i-751": {
    hero: {
      badge: "Form I-751 Package",
      title: "Remove Conditions on Green Card",
      subtitle: "Convert Your 2-Year Card to Permanent 10-Year Card • Only $139"
    },
    content: `
      <section class="py-16 lg:py-32 bg-gradient-to-b from-blue-50 to-white">
        <div class="max-w-5xl mx-auto px-6 text-center">
          <div class="bg-white rounded-3xl shadow-2xl p-16 border border-blue-200">
            <div class="text-8xl font-black text-gray-900 mb-4">$139 <span class="text-3xl text-gray-500">+ USCIS fees</span></div>
            <p class="text-3xl font-bold text-green-600 mb-10">Save $950+ vs average attorney</p>

            <div class="bg-gradient-to-r from-blue-600 to-blue-600 text-white rounded-3xl p-12 mb-12">
              <h3 class="text-4xl font-bold mb-10">Removal of Conditions Package Includes:</h3>
              <ul class="grid md:grid-cols-2 gap-6 text-left text-xl">
                <li class="flex items-center gap-4"><span class="text-3xl">✓</span> Form I-751 Petition</li>
                <li class="flex items-center gap-4"><span class="text-3xl">✓</span> Form G-1145</li>
                <li class="flex items-center gap-4"><span class="text-3xl">✓</span> Complete Evidence Checklist</li>
                <li class="flex items-center gap-4"><span class="text-3xl">✓</span> Personalized Instructions</li>
                <li class="flex items-center gap-4"><span class="text-3xl">✓</span> Live Expert Support</li>
                <li class="flex items-center gap-4"><span class="text-3xl">✓</span> Approval Guaranteed</li>
              </ul>
            </div>

            <a href="#" class="inline-block bg-blue-600 text-white font-bold text-2xl px-16 py-6 rounded-xl hover:bg-blue-700 transition shadow-lg">
              Start My I-751 Package Now →
            </a>
          </div>
        </div>
      </section>
    `
  }
};

export default greenCardData;